/**
 * Central API auth — ek jagah se saari axios + fetch calls authenticated hoti hain.
 * Import once in index.js before App.
 */
import axios from 'axios';

const TOKEN_KEY = 'token';
const AUTH_HEADER = 'Authorization';
let isSetupDone = false;

// Login endpoints — in par token attach nahi karna, response se token capture karna hai
const LOGIN_PATHS = [
  '/getUsersAuth',
  '/getUsersExt',
  '/getNMEOSUsersAuth',
  '/PasswordAuth',
  '/MobileAuth',
  '/useridpasswordauth',
  '/otpauth',
];

const isLoginRequest = (url = '') =>
  LOGIN_PATHS.some((path) => String(url).includes(path));

const isApiRequest = (url = '') => {
  const value = String(url);
  return value.includes('/api/') || value.includes('/appdevapi/api/') || value.includes('/appdevapi/api/MT');
};

export const getAuthToken = () =>
  sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || '';

export const setAuthToken = (token) => {
  if (!token || typeof token !== 'string') return;
  const clean = token.replace(/^Bearer\s+/i, '').trim();
  if (!clean) return;
  sessionStorage.setItem(TOKEN_KEY, clean);
  localStorage.setItem(TOKEN_KEY, clean);
};

export const clearAuthToken = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
};

export const persistLoginAuth = (payload) => {
  const token = extractToken(payload);
  if (token) setAuthToken(token);
  return token;
};

export const logoutAuth = () => {
  clearAuthToken();
  sessionStorage.removeItem('user');
};

const extractToken = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  const directToken =
    payload.token ||
    payload.accessToken ||
    payload.access_token ||
    payload.jwtToken ||
    payload.authToken ||
    payload.bearerToken;

  if (directToken) return directToken;

  const content = payload.dataContent;
  const firstRow = Array.isArray(content) ? content[0] : content;

  if (firstRow && typeof firstRow === 'object') {
    return (
      firstRow.token ||
      firstRow.accessToken ||
      firstRow.access_token ||
      firstRow.jwtToken ||
      firstRow.authToken ||
      firstRow.bearerToken ||
      null
    );
  }

  return null;
};

const extractTokenFromHeaders = (headers) => {
  if (!headers) return null;
  const raw =
    (typeof headers.get === 'function' && headers.get(AUTH_HEADER)) ||
    headers[AUTH_HEADER] ||
    headers.authorization ||
    headers.Authorization;
  if (!raw || typeof raw !== 'string') return null;
  return raw.replace(/^Bearer\s+/i, '').trim() || null;
};

const attachAuthHeader = (headers, token) => {
  if (!token) return headers;

  if (headers instanceof Headers) {
    if (!headers.has(AUTH_HEADER)) {
      headers.set(AUTH_HEADER, `Bearer ${token}`);
    }
    return headers;
  }

  const nextHeaders = { ...(headers || {}) };
  if (!nextHeaders[AUTH_HEADER] && !nextHeaders.Authorization) {
    nextHeaders[AUTH_HEADER] = `Bearer ${token}`;
  }
  return nextHeaders;
};

const handleUnauthorized = (url = '') => {
  if (isLoginRequest(url)) return;

  clearAuthToken();
  sessionStorage.removeItem('user');

  if (window.location.hash && window.location.hash !== '#/' && window.location.hash !== '#') {
    window.location.hash = '#/';
  }
};

const saveTokenFromPayload = (payload, headers) => {
  const token = extractToken(payload) || extractTokenFromHeaders(headers);
  if (token) setAuthToken(token);
};

const captureTokenFromLoginResponse = async (url, response) => {
  if (!isLoginRequest(url) || !response?.ok) return;

  try {
    const headerToken = extractTokenFromHeaders(response.headers);
    if (headerToken) {
      setAuthToken(headerToken);
      return;
    }
    const cloned = response.clone();
    const data = await cloned.json();
    saveTokenFromPayload(data);
  } catch (_) {
    // Ignore non-JSON login responses.
  }
};

const setupAuthInterceptors = () => {
  if (isSetupDone) return;
  isSetupDone = true;

  // ── Axios request: har API call pe Bearer token ──
  axios.interceptors.request.use((config) => {
    const url = config.url || '';
    const token = getAuthToken();

    if (token && isApiRequest(url) && !isLoginRequest(url)) {
      config.headers = attachAuthHeader(config.headers, token);
    }

    return config;
  });

  // ── Axios response: login se token save + 401 handle ──
  axios.interceptors.response.use(
    (response) => {
      const url = response.config?.url || '';
      if (isLoginRequest(url)) {
        saveTokenFromPayload(response.data, response.headers);
      }
      return response;
    },
    (error) => {
      const url = error.config?.url || '';
      if (error.response?.status === 401) {
        handleUnauthorized(url);
      }
      return Promise.reject(error);
    }
  );

  // ── fetch() patch: Login.js aur baaki fetch calls bhi cover ──
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const token = getAuthToken();
    const shouldAttachAuth = token && isApiRequest(url) && !isLoginRequest(url);

    const nextInit = {
      ...init,
      headers: shouldAttachAuth
        ? attachAuthHeader(init.headers, token)
        : init.headers,
    };

    const response = await originalFetch(input, nextInit);

    await captureTokenFromLoginResponse(url, response);

    if (response.status === 401 && isApiRequest(url) && !isLoginRequest(url)) {
      handleUnauthorized(url);
    }

    return response;
  };
};

setupAuthInterceptors();

export default axios;
